import type { Period } from '@/entities/types';
import {
  getPreviousPeriodStartAt,
  getYesterdayStartAt,
  toMidnightLocalString,
} from '@/utils/datetime';
import {
  formatQuarterRange,
  formatRelativePreviousDay,
  formatRelativePreviousQuarter,
  formatRelativePreviousWeek,
  formatRelativePreviousYear,
  formatRelativeQuarter,
  formatRelativeWeek,
  formatRelativeYear,
  formatWeekdayMonthDayYear,
  formatWeekRange,
  formatYearRange,
} from '@/utils/format-datetime';
import {
  useParentContextPlan,
  type ParentPlanCandidate,
} from '../../hooks/useParentContextPlan';
import { ContextCard } from './ContextCard';

interface ContextPaneProps {
  period: Period;
  targetStartAt: Date;
  className?: string;
}

function getParentDisplay(
  now: Date,
  parent: ParentPlanCandidate,
): { title: string; subtitle: string } {
  if (parent.period === 'week') {
    return {
      title: formatRelativeWeek(now, parent.startAt),
      subtitle: formatWeekRange(parent.startAt),
    };
  }
  if (parent.period === 'quarter') {
    return {
      title: formatRelativeQuarter(now, parent.startAt),
      subtitle: formatQuarterRange(parent.startAt),
    };
  }
  return {
    title: formatRelativeYear(now, parent.startAt),
    subtitle: formatYearRange(parent.startAt),
  };
}

function getPreviousPeriodDisplay(
  now: Date,
  prevStartAt: Date,
  period: Period,
): { title: string; subtitle: string } {
  if (period === 'day') {
    return {
      title: formatRelativePreviousDay(now, prevStartAt),
      subtitle: formatWeekdayMonthDayYear(prevStartAt),
    };
  }
  if (period === 'week') {
    return {
      title: formatRelativePreviousWeek(now, prevStartAt),
      subtitle: formatWeekRange(prevStartAt),
    };
  }
  if (period === 'quarter') {
    return {
      title: formatRelativePreviousQuarter(now, prevStartAt),
      subtitle: formatQuarterRange(prevStartAt),
    };
  }
  return {
    title: formatRelativePreviousYear(now, prevStartAt),
    subtitle: formatYearRange(prevStartAt),
  };
}

export function ContextPane({
  period,
  targetStartAt,
  className,
}: ContextPaneProps) {
  const now = new Date();
  const { parentContext } = useParentContextPlan(period, targetStartAt);

  const prevStartAt = getPreviousPeriodStartAt(targetStartAt, period);
  const prevStartAtLocal = toMidnightLocalString(prevStartAt);
  const prevDisplay = getPreviousPeriodDisplay(now, prevStartAt, period);

  if (period === 'day') {
    const yesterdayStartAt = getYesterdayStartAt(targetStartAt);
    const yesterdayStartAtLocal = toMidnightLocalString(yesterdayStartAt);
    const yesterdayDisplay = getPreviousPeriodDisplay(
      now,
      yesterdayStartAt,
      'day',
    );

    return (
      <div className={`space-y-4 ${className ?? ''}`}>
        {parentContext && (
          <ContextCard
            title={getParentDisplay(now, parentContext).title}
            subtitle={getParentDisplay(now, parentContext).subtitle}
            period={parentContext.period}
            startAtLocal={parentContext.startAtLocal}
          />
        )}
        <ContextCard
          title={yesterdayDisplay.title}
          subtitle={yesterdayDisplay.subtitle}
          period="day"
          startAtLocal={yesterdayStartAtLocal}
        />
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className ?? ''}`}>
      {parentContext && (
        <ContextCard
          title={getParentDisplay(now, parentContext).title}
          subtitle={getParentDisplay(now, parentContext).subtitle}
          period={parentContext.period}
          startAtLocal={parentContext.startAtLocal}
        />
      )}
      <ContextCard
        title={prevDisplay.title}
        subtitle={prevDisplay.subtitle}
        period={period}
        startAtLocal={prevStartAtLocal}
      />
    </div>
  );
}
